import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  Tab,
  TextField,
  Typography,
  useTheme
} from '@mui/material'
import React, { FC, useState } from 'react'

import { IProductRate, ProductType } from '../../../all-types'
import { CommonBtn } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../redux-store/hooks'
import { getFileUploadURL, updateProduct } from '../../../services'
import {
  ProductDiscount,
  ProductExtrasForm,
  ProductInstructionsForm,
  ProductTax,
} from '../components'
import { ProductRates } from '../components/product-rates'

interface EditPopupProps {
  editProduct: ProductType
  exit?: () => void
}

const EditProductPopup: FC<EditPopupProps> = ({ editProduct }) => {
  const theme = useTheme()
  const dispatcher = useAppDispatch()
  const categories = useAppSelector((state) => state.categories.data)

  const [product, setProduct] = useState<ProductType>({
    ...editProduct,
    rateCard: new Map(Object.entries(editProduct.rateCard)),
    defaults: new Map(Object.entries(editProduct.defaults)),
  })

  const [loading, setLoading] = useState(false)
  const [imgLoading, setImageLoading] = useState(false)
  const [tabValue, setTabValue] = useState('1')

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue)
  }

  const addRateCardItems = (
    newRateCard: IProductRate,
    defaults: IProductRate
  ) => {
    setProduct({ ...product, rateCard: newRateCard, defaults })
  }

  const updateProductDetails = async () => {
    try {
      setLoading(true)
      await dispatcher(
        updateProduct({
          ...product,
          rateCard: Object.fromEntries(product.rateCard),
          defaults: Object.fromEntries(product.defaults),
        })
      )
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const uploadImageToBucket = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setImageLoading(true)
      const newFile = event.target.files?.[0]
      if (newFile) {
        const imageURL = await getFileUploadURL(newFile)
        setProduct({ ...product, image: imageURL })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setImageLoading(false)
    }
  }

  if (loading) {
    return (
      <Stack
        sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography sx={{ mt: 2, fontWeight: 600, color: 'text.secondary' }}>
          Updating Product...
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={0} sx={{ height: '100%', bgcolor: 'background.default' }}>
      {/* --- Header Section --- */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: '12px',
              bgcolor: theme.palette.primary.main + '15',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <EditRoundedIcon sx={{ color: theme.palette.primary.main }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Edit Product
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {product.productCode}
            </Typography>
          </Box>
        </Stack>

        <CommonBtn
          variant="contained"
          onClick={updateProductDetails}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveAsIcon />}
          sx={{ borderRadius: '10px', px: 3 }}
        >
          Save Changes
        </CommonBtn>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Grid container spacing={4}>
          {/* --- Image Section --- */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                position: 'relative',
                height: 280,
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: theme.shadows[10],
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  backgroundImage: `url(${product?.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transition: 'transform 0.5s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  background:
                    'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                }}
              >
                <LoadingButton
                  component="label"
                  variant="contained"
                  loading={imgLoading}
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderRadius: '12px',
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  Change Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={uploadImageToBucket}
                  />
                </LoadingButton>
              </Box>
            </Box>
          </Grid>

          {/* --- Basic Info Section --- */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Product Name"
                variant="outlined"
                value={product.name}
                onChange={(e) =>
                  setProduct({ ...product, name: e.target.value })
                }
                InputProps={{ sx: { borderRadius: '12px' } }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    type="number"
                    value={product.productCode}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        productCode: parseInt(e.target.value, 10),
                      })
                    }
                    InputProps={{ sx: { borderRadius: '12px' } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Select
                    fullWidth
                    value={product.isInStock ? 'stock' : 'outOfStock'}
                    onChange={(e) =>
                      setProduct({
                        ...product,
                        isInStock: e.target.value === 'stock',
                      })
                    }
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="stock">Available</MenuItem>
                    <MenuItem value="outOfStock">Out of Stock</MenuItem>
                  </Select>
                </Grid>
              </Grid>

              <Autocomplete
                fullWidth
                options={categories}
                getOptionLabel={(option) => option.categoryName}
                value={
                  categories.find((c) => c.categoryId === product.categoryId) ||
                  null
                }
                onChange={(_, newValue) => {
                  setProduct({
                    ...product,
                    categoryId: newValue?.categoryId || '',
                    category: newValue?.categoryName || '',
                  })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    required
                    InputProps={{
                      ...params.InputProps,
                      sx: { borderRadius: '12px' },
                    }}
                  />
                )}
              />
            </Stack>
          </Grid>

          {/* --- Tabbed Features Section --- */}
          <Grid item xs={12}>
            <TabContext value={tabValue}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: '16px',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                }}
              >
                <TabList
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 1,
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                    },
                  }}
                >
                  <Tab
                    label="Pricing"
                    value="1"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Taxation"
                    value="2"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Offers"
                    value="3"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Instructions"
                    value="4"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                  />
                  <Tab
                    label="Extras"
                    value="5"
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                    disabled
                  />
                </TabList>

                <Box sx={{ p: 1 }}>
                  <TabPanel value="1">
                    <ProductRates
                      allRate={product.rateCard}
                      allDefaults={product.defaults}
                      setAllRate={addRateCardItems}
                    />
                  </TabPanel>
                  <TabPanel value="2">
                    <ProductTax
                      data={product.tax}
                      onChange={(data) => setProduct({ ...product, tax: data })}
                    />
                  </TabPanel>
                  <TabPanel value="3">
                    <ProductDiscount
                      data={product.discounts}
                      onChange={(data) =>
                        setProduct({ ...product, discounts: data })
                      }
                    />
                  </TabPanel>
                  <TabPanel value="4">
                    <ProductInstructionsForm
                      instructions={product.productInstructions}
                      setInstructions={(data) =>
                        setProduct({ ...product, productInstructions: data })
                      }
                    />
                  </TabPanel>
                  <TabPanel value="5">
                    <ProductExtrasForm
                      extras={product.productExtras}
                      setExtras={(data) =>
                        setProduct({ ...product, productExtras: data })
                      }
                    />
                  </TabPanel>
                </Box>
              </Box>
            </TabContext>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export { EditProductPopup }
